import type { KeyboardEvent, PointerEvent, Ref } from "react";
import type { RowData } from "@tanstack/react-table";
import { styleWithVars } from "@lib/cssVars";
import { useEditingHandlers } from "../editing/useEditingHandlers";
import { GridDnd } from "../features/dnd/GridDnd";
import { LoadMoreStatus } from "../features/loadMore/LoadMoreRows";
import { RowDragOutSource, useRowDragOut } from "../features/dnd/rowDragOut";
import { RangeActionSource } from "../features/range/RangeActionSlot";
import { useRangeMouse } from "../features/range/useRangeMouse";
import type { GridController } from "../focus/gridController";
import { useGridKeyboard } from "../focus/useGridKeyboard";
import { useIsFocusInRows } from "../focus/useKeepFocusInRows";
import { ColumnSizeVars } from "../layout/ColumnSizeVars";
import { HEADER_ROW_HEIGHT, ROW_HEIGHT_BY_DENSITY } from "../layout/density";
import { SkeletonRows } from "../states/SkeletonRows";
import { getStatusFlags } from "../states/statusFlags";
import type { DataGridInstance } from "../types";
import { Body } from "./Body";
import { Header } from "./Header";
import { hasTotals, TotalsRow } from "./TotalsRow";
import { useBorderResize } from "./useBorderResize";
import { useScrollbarSizeVars } from "./useScrollbarSizeVars";
import { useScrollEndInset } from "./useScrollEndInset";
import { useViewportObservers } from "./useViewportObservers";
import styles from "./Grid.module.scss";

interface GridProps<TData extends RowData> {
    instance: DataGridInstance<TData>;
    controller: GridController;
    rootElement: HTMLDivElement | null;
    messageLayerRef: Ref<HTMLDivElement>;
    ariaLabel: string | undefined;
    ariaLabelledby: string | undefined;
}

/** Status flags, sticky heights, and the row count the grid announces. */
const getGridMetrics = <TData extends RowData>(
    instance: DataGridInstance<TData>,
) => {
    const { table, status, totals, density } = instance;
    const headerRowCount = table.getHeaderGroups().length;
    const { isLoading, isReady, isRefetching, hasRows } = getStatusFlags({
        status,
        rowCount: table.getRowModel().rows.length,
    });
    const shownTotals = hasRows && hasTotals(table) ? totals : undefined;
    const totalsRowCount = shownTotals === undefined ? 0 : 1;
    return {
        headerRowCount,
        headerHeight: headerRowCount * HEADER_ROW_HEIGHT,
        isLoading,
        isRefetching,
        isLoadingMore: Boolean(isReady && instance.loadMore?.isLoading),
        isReady,
        hasRows,
        shownTotals,
        footerHeight:
            shownTotals === undefined ? 0 : ROW_HEIGHT_BY_DENSITY[density],
        ariaRowCount:
            instance.hasMoreRows && instance.loadMore?.rowCount === undefined
                ? -1
                : headerRowCount + instance.totalRowCount + totalsRowCount,
    };
};

/**
 * The single scroll viewport (`role="grid"`/`"treegrid"`): sticky header,
 * virtualized body or skeleton rows, the sticky totals row, and the column
 * size vars. One tab stop; all grid keys are handled here. Its frame also
 * holds the layer the empty and error messages render into.
 */
export const Grid = <TData extends RowData>({
    instance,
    controller,
    rootElement,
    messageLayerRef,
    ariaLabel,
    ariaLabelledby,
}: Readonly<GridProps<TData>>) => {
    const { table, isTree, totalRowCount } = instance;
    const editing = useEditingHandlers({ instance, controller });
    const keyboard = useGridKeyboard({
        instance,
        controller,
        onCellKey: editing.onCellKey,
    });
    const rangeMouse = useRangeMouse({ instance, controller });
    const borderResize = useBorderResize({ table, controller });
    const { sourceRef: rowDragOutRef, onPointerDown: startRowDragOut } =
        useRowDragOut(instance);
    const { onPointerDown: recordListPress } = keyboard;
    const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
        recordListPress();
        borderResize.onPointerDown(event);
        startRowDragOut(event);
    };
    const handleKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
        borderResize.onKeyDownCapture(event);
        keyboard.onKeyDownCapture(event);
    };
    const { viewportRef, viewportElement } = useViewportObservers(controller);
    const { spacerRef: scrollEndRef, inset: scrollEndInset } =
        useScrollEndInset();
    useScrollbarSizeVars(viewportElement, rootElement);
    const isFocusInRows = useIsFocusInRows(table);

    const {
        headerRowCount,
        headerHeight,
        isLoading,
        isRefetching,
        isLoadingMore,
        isReady,
        hasRows,
        shownTotals,
        footerHeight,
        ariaRowCount,
    } = getGridMetrics(instance);
    if (!isReady || isRefetching || isLoadingMore) {
        controller.rowMotion.noteLoading();
    } else {
        controller.rowMotion.noteSettled();
    }
    return (
        <>
            <div
                className={styles.frame}
                style={styleWithVars({
                    "--dg-header-h": `${headerHeight}px`,
                    "--dg-footer-h": `${footerHeight}px`,
                })}
            >
                <div
                    ref={viewportRef}
                    role={isTree ? "treegrid" : "grid"}
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledby}
                    aria-rowcount={ariaRowCount}
                    aria-colcount={table.getVisibleLeafColumns().length}
                    aria-busy={
                        isLoading || isRefetching || isLoadingMore || undefined
                    }
                    aria-multiselectable={
                        table.options.enableRowSelection ? true : undefined
                    }
                    tabIndex={isFocusInRows ? -1 : 0}
                    className={styles.viewport}
                    data-refetching={isRefetching || undefined}
                    data-range-selection={
                        instance.hasRangeSelection || undefined
                    }
                    data-row-open={
                        (instance.hasRowOpen && !instance.hasRangeSelection) ||
                        undefined
                    }
                    onKeyDown={keyboard.onKeyDown}
                    onKeyDownCapture={handleKeyDownCapture}
                    onFocus={keyboard.onFocus}
                    onBlur={keyboard.onBlur}
                    onClick={keyboard.onClick}
                    onPointerDown={handlePointerDown}
                    onPointerMove={borderResize.onPointerMove}
                    onPointerLeave={borderResize.onPointerLeave}
                    onScroll={borderResize.onScroll}
                    onMouseDown={rangeMouse.onMouseDown}
                    onMouseOver={rangeMouse.onMouseOver}
                    onDoubleClick={editing.onDoubleClick}
                    onCopy={editing.onCopy}
                    onCut={editing.onCut}
                    onPaste={editing.onPaste}
                >
                    {instance.hasRowDragOut ? (
                        <RowDragOutSource
                            apiRef={rowDragOutRef}
                            controller={controller}
                        />
                    ) : null}
                    <RangeActionSource atoms={instance.atoms}>
                        <GridDnd instance={instance} controller={controller}>
                            <ColumnSizeVars
                                table={table}
                                controller={controller}
                                rootElement={rootElement}
                                columnLayout={instance.columnLayout}
                            />
                            <Header
                                table={table}
                                controller={controller}
                                isRefetching={isRefetching}
                                columnVariants={instance.atoms.columnVariants}
                            />
                            {isLoading ? <SkeletonRows table={table} /> : null}
                            {isReady ? (
                                <Body
                                    instance={instance}
                                    controller={controller}
                                    viewportElement={viewportElement}
                                    headerHeight={headerHeight}
                                    headerRowCount={headerRowCount}
                                    footerHeight={footerHeight + scrollEndInset}
                                />
                            ) : null}
                            {shownTotals === undefined ? null : (
                                <TotalsRow
                                    table={table}
                                    ariaRowIndex={
                                        headerRowCount + totalRowCount + 1
                                    }
                                    totals={shownTotals}
                                    validateTotal={instance.validateTotal}
                                    isSeparator={instance.rowKinds.isSeparator}
                                />
                            )}
                            {hasRows ? (
                                <div
                                    ref={scrollEndRef}
                                    className={styles.scrollEndSpacer}
                                    aria-hidden
                                />
                            ) : null}
                        </GridDnd>
                    </RangeActionSource>
                </div>
                <div ref={messageLayerRef} className={styles.messageLayer} />
            </div>
            <LoadMoreStatus instance={instance} />
        </>
    );
};
